import { IconChevronRight } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import type { HealthRow } from "#/types/dataHealth";

interface Props {
  row: HealthRow;
  /** The page where this record is fixed, or null when the reader cannot open it from here. */
  href: string | null;
  countOf: "items" | "vehicles" | null;
}

/**
 * One affected record: its name, the empty columns as pills, how many
 * records sit behind it, and a chevron when it opens the page to fix it.
 */
const HealthRowItem = ({ row, href, countOf }: Props) => {
  const t = usePrvTranslation();
  const navigate = useNavigate();

  const content = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-sm font-medium text-gray-900">{row.name}</span>
        <span className="flex flex-wrap items-center gap-1">
          {row.fields.map((field) => (
            <span
              key={field}
              className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] leading-4 text-gray-700"
            >
              {t(`dataHealth.fields.${field}`, { defaultValue: field })}
            </span>
          ))}
          {countOf && row.count !== null && (
            <span className="text-xs text-gray-500 tabular-nums">
              {t(`dataHealth.counts.${countOf}`, { count: row.count })}
            </span>
          )}
        </span>
      </span>
      {href && <IconChevronRight size={16} className="shrink-0 text-gray-400" />}
    </>
  );

  const base =
    "flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left";
  if (!href) return <div className={base}>{content}</div>;
  return (
    <button
      type="button"
      onClick={() => void navigate(href)}
      className={`${base} transition-colors hover:border-gray-300 hover:bg-gray-50`}
    >
      {content}
    </button>
  );
};

export default HealthRowItem;
